import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar, GridColumnVisibilityModel } from '@mui/x-data-grid';
import ConfirmationDialog from '../DialogBox';

interface CommonDataGridProps {
  id: string;
  rows: any[];
  columns: GridColDef[];
  loading: boolean;
  showConfirmation: boolean;
  confirmationMessage: string;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  toolbarProps?: any;
  paginationMode?: 'client' | 'server';
  rowCount?: number;
  setFilterModel?: any;
  setSortingModel?: any;
  paginationModel?: { page: number; pageSize: number };
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  pageSizeOptions?: number[];
}

const CommonDataGrid: React.FC<CommonDataGridProps> = ({
  id,
  rows,
  columns,
  loading,
  showConfirmation,
  confirmationMessage,
  onCancelDelete,
  onConfirmDelete,
  toolbarProps,
  setFilterModel = () => {},
  setSortingModel = () => {},
  paginationMode = 'client',
  rowCount,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  /** -------------------
   *  Load persisted widths
   --------------------*/
  const storedWidths = React.useMemo(() => {
    const saved = localStorage.getItem(`gridWidths_${id}`);
    return saved ? JSON.parse(saved) : {};
  }, [id]);

  const [columnDefs, setColumnDefs] = useState<GridColDef[]>(
    columns.map((col) => {
      if (storedWidths[col.field]) {
        return { ...col, width: storedWidths[col.field], flex: undefined };
      }
      return col;
    })
  );

  /** -------------------
   *  Load persisted visibility
   --------------------*/
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => {
      const saved = localStorage.getItem(`gridVisibility_${id}`);
      return saved ? JSON.parse(saved) : {};
    });

  /** -------------------
   *  Handle column resize
   --------------------*/
  const handleColumnResize = (params: { colDef: GridColDef; width: number }) => {
    setColumnDefs((prev) =>
      prev.map((col) =>
        col.field === params.colDef.field
          ? { ...col, width: params.width, flex: undefined }
          : col
      )
    );

    // Store only width for that column
    const updatedWidths = {
      ...storedWidths,
      [params.colDef.field]: params.width,
    };
    localStorage.setItem(`gridWidths_${id}`, JSON.stringify(updatedWidths));
  };

  /** -------------------
   *  Handle column visibility changes
   --------------------*/
  const handleVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(`gridVisibility_${id}`, JSON.stringify(newModel));
  };

  const dataGridStyling = {
  height: 'auto',
  width: '100%',
  overflowX: 'auto',
  overflowY: 'auto',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  
  '& .MuiDataGrid-root': {
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  
  '& .MuiDataGrid-main': {
    borderRadius: '12px',
  },
  
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f1f5f9',
    padding: '16px 12px',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    transition: 'all 0.2s ease-in-out',
    '&:focus': {
      outline: 'none',
      backgroundColor: '#f8fafc',
    },
    '&:focus-within': {
      outline: '2px solid #3b82f6',
      outlineOffset: '-2px',
    },
  },
  
  '& .MuiDataGrid-row': {
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8fafc',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
    },
    '&.Mui-selected': {
      backgroundColor: '#eff6ff',
      '&:hover': {
        backgroundColor: '#dbeafe',
      },
    },
    '&:last-child .MuiDataGrid-cell': {
      borderBottom: 'none',
    },
  },
  
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    fontWeight: '700',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid #e2e8f0',
    borderRadius: '12px 12px 0 0',
    minHeight: '56px !important',
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: '700',
      fontSize: '13px',
    },
    '& .MuiDataGrid-columnSeparator': {
      color: '#cbd5e1',
    },
  },
  
  '& .MuiDataGrid-columnHeader': {
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  
  '& .MuiDataGrid-toolbarContainer': {
    borderBottom: '1px solid #e2e8f0',
    padding: '16px 20px',
    backgroundColor: '#ffffff',
    borderRadius: '12px 12px 0 0',
    minHeight: '64px',
    '& .MuiButton-root': {
      color: '#3b82f6',
      fontSize: '14px',
      textTransform: 'none',
      fontWeight: '600',
      padding: '8px 16px',
      borderRadius: '8px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#eff6ff',
        transform: 'translateY(-1px)',
      },
    },
    '& .MuiButton-text': {
      '&:hover': {
        backgroundColor: '#f1f5f9',
      },
    },
  },
  
  '& .MuiDataGrid-footerContainer': {
    borderTop: '2px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    padding: '12px 20px',
    borderRadius: '0 0 12px 12px',
    minHeight: '56px',
    '& .MuiTablePagination-root': {
      color: '#64748b',
    },
    '& .MuiTablePagination-selectLabel': {
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
    },
    '& .MuiTablePagination-displayedRows': {
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
    },
    '& .MuiSelect-select': {
      fontSize: '14px',
      padding: '6px 12px',
      borderRadius: '6px',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      '&:focus': {
        backgroundColor: '#ffffff',
        borderColor: '#3b82f6',
      },
    },
    '& .MuiIconButton-root': {
      color: '#3b82f6',
      padding: '8px',
      borderRadius: '8px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#eff6ff',
        transform: 'translateY(-1px)',
      },
      '&.Mui-disabled': {
        color: '#cbd5e1',
        '&:hover': {
          backgroundColor: 'transparent',
          transform: 'none',
        },
      },
    },
  },
  
  '& .MuiDataGrid-selectedRowCount': {
    display: 'none',
  },
  
  // Enhanced scrollbar styling
  '& .MuiDataGrid-virtualScroller': {
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f5f9',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#94a3b8',
      },
    },
  },
  
  // Loading overlay styling
  '& .MuiDataGrid-overlay': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(2px)',
  },
  
  // Menu styling
  '& .MuiDataGrid-menuIcon': {
    color: '#64748b',
    '&:hover': {
      color: '#3b82f6',
    },
  },
  
  // Checkbox styling
  '& .MuiCheckbox-root': {
    color: '#64748b',
    '&.Mui-checked': {
      color: '#3b82f6',
    },
    '&:hover': {
      backgroundColor: '#eff6ff',
    },
  },
  
  // Sort icon styling
  '& .MuiDataGrid-sortIcon': {
    color: '#64748b',
    '&.MuiDataGrid-sortIcon--desc': {
      color: '#3b82f6',
    },
    '&.MuiDataGrid-sortIcon--asc': {
      color: '#3b82f6',
    },
  },
  
  // Filter icon styling
  '& .MuiDataGrid-filterIcon': {
    color: '#64748b',
    '&:hover': {
      color: '#3b82f6',
    },
  },
};

  return (
    <Box
      sx={dataGridStyling}
    >
      {showConfirmation && (
        <ConfirmationDialog
          message={confirmationMessage}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
        />
      )}

      <DataGrid
        rows={rows}
        columns={columnDefs}
        getRowId={(row: { _id: any }) => row._id}
        autoHeight
        slotProps={toolbarProps}
        loading={loading}
        disableRowSelectionOnClick
        onFilterModelChange={setFilterModel}
        onSortModelChange={setSortingModel}
        paginationMode={paginationMode}
        rowCount={paginationMode === 'server' ? rowCount : undefined}
        paginationModel={paginationMode === 'server' ? paginationModel : undefined}
        onPaginationModelChange={paginationMode === 'server' ? onPaginationModelChange : undefined}
        pageSizeOptions={pageSizeOptions}
        initialState={
          paginationMode === 'client'
            ? {
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }
            : undefined
        }
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:focus': {
            outline: 'none',
          },
        }}
        onColumnWidthChange={handleColumnResize}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleVisibilityChange}
      />
    </Box>
  );
};

export default CommonDataGrid;
